import {
  BookingStatus,
  RegisterButtonLocation,
  ShiftRepeat,
} from "@repo/database";
import { createFileRoute } from "@tanstack/react-router";
import { getDay, isAfter, isBefore, isSameDay } from "date-fns";
import { PlusIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { OccupationFlowChart } from "~/components/booking/charts/occupation-flow-chart";
import { FunctionButtons } from "~/components/button/function-buttons";
import { DateNav } from "~/components/miscellaneous/date-nav";
import { Button } from "~/components/ui/button";
import { useBooking } from "~/hooks/use-booking";
import { useButton } from "~/hooks/use-button";
import { useMediaQuery } from "~/hooks/use-media-query";
import { useShift } from "~/hooks/use-shift";
import { cn } from "~/lib/utils";
import { useBookingManager } from "~/managers/use-booking-manager";
import { useTempBookingStore } from "~/stores/use-booking-store";

export const Route = createFileRoute("/_main/")({
  component: DashboardPage,
});

function DashboardPage() {
  const { bookings } = useBooking();

  const { buttons } = useButton();

  const { shifts } = useShift();

  const { selectedDate, setSelectedDate } = useTempBookingStore();

  const { t } = useTranslation("common");

  const { openBookingDialog } = useBookingManager();

  const day = getDay(selectedDate);

  const isMobile = useMediaQuery(768);

  const dashboardButtons = buttons.filter((button) =>
    button.location === RegisterButtonLocation.dashboard_footer && button.mobile === isMobile
  );

  const matchedShifts = shifts
    .filter(
      (shift) =>
        isAfter(selectedDate, shift.startDate) &&
        (shift.endDate ? isBefore(selectedDate, shift.endDate) : true),
    )
    .filter(
      (shift) =>
        (shift.repeat === ShiftRepeat.weekly &&
          shift.repeatDays.includes(day)) ||
        (shift.repeat === ShiftRepeat.never &&
          isSameDay(shift.startDate, selectedDate)),
    );

  const bookingsToday = bookings.filter(
    (booking) =>
      isSameDay(booking.startTime, selectedDate) &&
      booking.bookingStatus !== BookingStatus.canceled &&
      booking.bookingStatus !== BookingStatus.no_show,
  );

  return (
    <div className={`flex flex-col h-full overflow-auto w-full`}>
      <div className="flex flex-col-reverse">
        {!isSameDay(selectedDate, new Date()) && (
          <div className="w-full h-10">
            <Button
              className={cn(
                "text-sm p-1 h-10 animate-pulse w-full rounded-none -left-40",
              )}
              onClick={() => setSelectedDate(new Date())}
              variant="warning"
            >
              {t("DateNav.today")}
            </Button>
          </div>
        )}

        <div className="flex items-center justify-center border-b">
          <DateNav
            futureDates={true}
            date={selectedDate}
            onNavigate={(nextDate) => setSelectedDate(nextDate)}
          />
        </div>
      </div>

      <div className="overflow-auto flex-1">
        <OccupationFlowChart
          bookings={bookingsToday}
          shifts={matchedShifts}
          selectedDate={selectedDate}
        />
      </div>

      {dashboardButtons.length === 0 && (
        <Button
          onClick={() => {
            void openBookingDialog();
          }}
          className="w-14 h-14 rounded-full items-center absolute right-4 bottom-4 z-[999]"
          variant="brand"
        >
          <PlusIcon />
        </Button>
      )}

      <div className="border-t">
        <FunctionButtons location={RegisterButtonLocation.dashboard_footer} />
      </div>
    </div>
  );
}
